import { create } from 'zustand'
import api from '../services/api'
import { usePermissionStore } from './permissionStore'

export interface AziendaInfo {
  id: string
  codice: string
  nome: string
  role: string
  logo_url?: string | null
}

interface User {
  user_id: string
  email: string
  username: string
  user_type: 'superadmin' | 'tenant_user'
  tenant_id?: string
  tenant_code?: string
  tenant_name?: string
  role: string
  // Multi-azienda
  azienda_id?: string
  azienda_codice?: string
  azienda_nome?: string
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  mustChangePassword: boolean
  // Multi-azienda
  aziende: AziendaInfo[]
  needsAziendaSelection: boolean

  login: (email: string, password: string, tenantCode?: string) => Promise<boolean>
  logout: () => Promise<void>
  checkAuth: () => void
  clearMustChangePassword: () => void
  selectAzienda: (aziendaId: string) => Promise<boolean>
}

/**
 * Restituisce il tenant_code salvato in localStorage.
 */
export function getSavedTenantCode(): string | null {
  return localStorage.getItem('erp_tenant_code')
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: JSON.parse(localStorage.getItem('erp_user') || 'null'),
  token: localStorage.getItem('erp_token'),
  isAuthenticated: !!localStorage.getItem('erp_token'),
  isLoading: false,
  error: null,
  mustChangePassword: false,
  aziende: JSON.parse(localStorage.getItem('erp_aziende') || '[]'),
  needsAziendaSelection: false,

  login: async (email: string, password: string, tenantCode?: string) => {
    set({ isLoading: true, error: null })

    try {
      const response = await api.post('/v1/auth/login', {
        email,
        password,
        tenant_code: tenantCode || null,
      })

      const data = response.data
      const user: User = {
        user_id: data.user_id,
        email: data.email,
        username: data.username,
        user_type: data.user_type,
        tenant_id: data.tenant_id,
        tenant_code: data.tenant_code,
        tenant_name: data.tenant_name,
        role: data.role,
        azienda_id: data.azienda_id,
        azienda_codice: data.azienda_codice,
        azienda_nome: data.azienda_nome,
      }

      const aziende: AziendaInfo[] = data.aziende || []

      localStorage.setItem('erp_token', data.access_token)
      localStorage.setItem('erp_user', JSON.stringify(user))
      localStorage.setItem('erp_aziende', JSON.stringify(aziende))

      if (data.tenant_code) {
        localStorage.setItem('erp_tenant_code', data.tenant_code)
      }

      set({
        user,
        token: data.access_token,
        isAuthenticated: true,
        isLoading: false,
        mustChangePassword: !!data.must_change_password,
        aziende,
        needsAziendaSelection: !!data.needs_azienda_selection,
      })

      // Carica permessi se azienda gia' selezionata
      if (data.user_type === 'tenant_user' && !data.needs_azienda_selection) {
        usePermissionStore.getState().loadPermissions()
      }

      return true
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Errore di autenticazione'
      set({ error: message, isLoading: false })
      return false
    }
  },

  selectAzienda: async (aziendaId: string) => {
    set({ isLoading: true, error: null })

    try {
      const response = await api.post('/v1/auth/select-azienda', {
        azienda_id: aziendaId,
      })

      const data = response.data
      const user: User = {
        user_id: data.user_id,
        email: data.email,
        username: data.username,
        user_type: data.user_type,
        tenant_id: data.tenant_id,
        tenant_code: data.tenant_code,
        tenant_name: data.tenant_name,
        role: data.role,
        azienda_id: data.azienda_id,
        azienda_codice: data.azienda_codice,
        azienda_nome: data.azienda_nome,
      }

      const aziende: AziendaInfo[] = data.aziende || get().aziende

      localStorage.setItem('erp_token', data.access_token)
      localStorage.setItem('erp_user', JSON.stringify(user))
      localStorage.setItem('erp_aziende', JSON.stringify(aziende))

      set({
        user,
        token: data.access_token,
        isLoading: false,
        aziende,
        needsAziendaSelection: false,
      })

      // Ricarica permessi con il nuovo ruolo azienda
      usePermissionStore.getState().loadPermissions()

      return true
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Errore selezione azienda'
      set({ error: message, isLoading: false })
      return false
    }
  },

  logout: async () => {
    try {
      await api.post('/v1/auth/logout')
    } catch (error) {
      console.warn('Errore durante logout API:', error)
    }

    localStorage.removeItem('erp_token')
    localStorage.removeItem('erp_user')
    localStorage.removeItem('erp_aziende')
    usePermissionStore.getState().clearPermissions()
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      mustChangePassword: false,
      aziende: [],
      needsAziendaSelection: false,
    })
  },

  clearMustChangePassword: () => set({ mustChangePassword: false }),

  checkAuth: () => {
    const token = localStorage.getItem('erp_token')
    const user = JSON.parse(localStorage.getItem('erp_user') || 'null')
    const aziende = JSON.parse(localStorage.getItem('erp_aziende') || '[]')

    if (token && user) {
      set({ token, user, isAuthenticated: true, aziende })
    } else {
      set({ token: null, user: null, isAuthenticated: false, aziende: [] })
    }
  },
}))
